import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Heart, Download, Loader2, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AMOUNTS = { EUR: [10, 25, 50, 100], XOF: [5000, 15000, 30000, 60000], XAF: [5000, 15000, 30000, 60000], USD: [10, 25, 50, 100] };
const SYMBOLS = { EUR: '€', XOF: 'FCFA', XAF: 'FCFA', USD: '$' };

export default function Donate() {
    const [currency, setCurrency] = useState('EUR');
    const [amount, setAmount] = useState(25);
    const [form, setForm] = useState({ donor_name: '', donor_email: '', donor_organization: '', message: '', is_public: false });
    const [busy, setBusy] = useState(false);
    const [success, setSuccess] = useState(null);
    const [recent, setRecent] = useState([]);

    useEffect(() => {
        axios.get(`${API}/donations/recent?limit=10`).then(r => setRecent(r.data.items || [])).catch(() => {});
    }, [success]);

    const submit = async (e) => {
        e.preventDefault();
        if (amount < 1) { toast.error('Montant minimum 1'); return; }
        setBusy(true);
        try {
            const r = await axios.post(`${API}/donations/create`, { ...form, amount: Number(amount), currency });
            setSuccess(r.data);
            toast.success('Merci pour votre don ! Le reçu PDF est prêt.');
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
        setBusy(false);
    };

    if (success) {
        const receiptUrl = `${process.env.REACT_APP_BACKEND_URL}${success.receipt_url}`;
        return (
            <div className="min-h-screen bg-background flex items-center justify-center p-6">
                <div className="max-w-lg w-full card-soft p-10 text-center" data-testid="donation-success">
                    <div className="h-16 w-16 rounded-full bg-primary-light text-primary mx-auto flex items-center justify-center mb-5">
                        <CheckCircle2 size={32} />
                    </div>
                    <h1 className="font-heading text-3xl mb-3">Merci pour votre soutien</h1>
                    <p className="text-foreground/70 mb-2">Votre don de <strong>{success.amount} {SYMBOLS[currency]}</strong> est enregistré.</p>
                    <p className="text-xs text-muted-foreground mb-6">Référence : <code>{success.receipt_number}</code></p>
                    <a href={receiptUrl} target="_blank" rel="noreferrer" download>
                        <Button className="bg-primary hover:bg-primary-hover" data-testid="donation-receipt-btn">
                            <Download size={14} className="mr-2" /> Télécharger le reçu PDF
                        </Button>
                    </a>
                    <div className="mt-6">
                        <Link to="/" className="text-sm text-primary underline">Retour à l'accueil</Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background">
            <header className="border-b-2 border-secondary bg-surface">
                <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3">
                        <img src="/african-valley-logo.png" alt="African Valley" className="h-11 w-11 rounded-lg object-cover ring-1 ring-border shadow-soft" />
                        <span className="font-heading text-2xl">
                            <span className="text-foreground">African</span> <span className="text-primary">Valley</span>
                        </span>
                    </Link>
                    <Link to="/auth/signin" className="text-sm text-foreground/70 hover:text-foreground">Connexion</Link>
                </div>
            </header>
            <main className="max-w-5xl mx-auto px-6 py-12 lg:py-16 grid lg:grid-cols-5 gap-10">
                <div className="lg:col-span-3 space-y-6">
                    <div className="eyebrow">Soutenir la plateforme</div>
                    <h1 className="font-heading text-4xl lg:text-5xl">Aidez African Valley à <span className="text-primary">grandir</span></h1>
                    <p className="text-foreground/70 leading-relaxed">
                        African Valley est un projet africain souverain de pilotage de projets financés. Vos dons financent
                        le développement continu, l'accompagnement des porteurs de projets et l'accès gratuit à nos modules
                        de formation. <strong>Montant libre, devises EUR/XOF/XAF/USD.</strong>
                    </p>

                    <form onSubmit={submit} className="card-soft p-7 space-y-5">
                        <div>
                            <div className="text-sm font-medium mb-2">Devise</div>
                            <div className="flex gap-2 flex-wrap">
                                {Object.keys(AMOUNTS).map(c => (
                                    <button type="button" key={c} onClick={() => { setCurrency(c); setAmount(AMOUNTS[c][1]); }}
                                        className={`px-4 py-2 rounded-md text-sm border transition-all ${currency === c ? 'bg-primary text-primary-foreground border-primary' : 'border-input hover:bg-surface-muted'}`}
                                        data-testid={`donate-currency-${c}`}>
                                        {c}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div>
                            <div className="text-sm font-medium mb-2">Montant</div>
                            <div className="flex gap-2 flex-wrap mb-3">
                                {AMOUNTS[currency].map(v => (
                                    <button type="button" key={v} onClick={() => setAmount(v)}
                                        className={`px-4 py-2 rounded-md text-sm border ${amount === v ? 'bg-primary-light text-primary border-primary' : 'border-input hover:bg-surface-muted'}`}
                                        data-testid={`donate-amount-${v}`}>
                                        {v} {SYMBOLS[currency]}
                                    </button>
                                ))}
                            </div>
                            <div className="relative">
                                <Input type="number" min="1" step="1" value={amount}
                                    onChange={(e) => setAmount(e.target.value)}
                                    className="pl-12 text-lg font-medium" data-testid="donate-amount-input" />
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground/60">{SYMBOLS[currency]}</span>
                            </div>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-3">
                            <Input placeholder="Votre nom (optionnel)" value={form.donor_name}
                                onChange={(e) => setForm({ ...form, donor_name: e.target.value })} data-testid="donate-name" />
                            <Input type="email" placeholder="Email pour le reçu (optionnel)" value={form.donor_email}
                                onChange={(e) => setForm({ ...form, donor_email: e.target.value })} data-testid="donate-email" />
                        </div>
                        <Input placeholder="Organisation (optionnel)" value={form.donor_organization}
                            onChange={(e) => setForm({ ...form, donor_organization: e.target.value })} data-testid="donate-org" />
                        <Textarea placeholder="Message d'encouragement (optionnel, 500 caractères max)" value={form.message}
                            onChange={(e) => setForm({ ...form, message: e.target.value })} maxLength={500} rows={3}
                            data-testid="donate-message" />
                        <label className="flex items-center gap-2 text-sm text-foreground/70 cursor-pointer">
                            <Checkbox checked={form.is_public}
                                onCheckedChange={(v) => setForm({ ...form, is_public: !!v })}
                                data-testid="donate-public" />
                            Afficher mon don publiquement dans la liste des soutiens (anonyme si nom non renseigné)
                        </label>
                        <div className="pt-2 border-t border-border">
                            <Button type="submit" disabled={busy} className="w-full bg-primary hover:bg-primary-hover h-12 text-base"
                                data-testid="donate-submit">
                                {busy ? <Loader2 size={16} className="animate-spin mr-2" /> : <Heart size={16} className="mr-2" />}
                                Faire un don de {amount} {SYMBOLS[currency]} (mock)
                            </Button>
                            <p className="text-xs text-muted-foreground mt-2 text-center">Paiement mocké pour la phase de test.</p>
                        </div>
                    </form>
                </div>

                <aside className="lg:col-span-2 space-y-4">
                    <div className="card-soft p-6">
                        <div className="eyebrow mb-3">Soutiens récents</div>
                        {recent.length === 0 ? (
                            <p className="text-sm text-foreground/60">Soyez le premier à soutenir.</p>
                        ) : (
                            <ul className="space-y-3" data-testid="donate-recent-list">
                                {recent.map((d, i) => (
                                    <li key={i} className="flex items-start justify-between gap-3 pb-3 border-b border-border last:border-0">
                                        <div className="min-w-0">
                                            <div className="text-sm font-medium truncate">{d.donor_name || 'Anonyme'}</div>
                                            {d.donor_organization && <div className="text-xs text-muted-foreground truncate">{d.donor_organization}</div>}
                                            {d.message && <div className="text-xs text-foreground/60 italic mt-1 line-clamp-2">« {d.message} »</div>}
                                        </div>
                                        <div className="text-sm font-semibold text-primary shrink-0">{Number(d.amount).toFixed(0)} {SYMBOLS[d.currency] || d.currency}</div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                    <div className="card-soft p-6 bg-surface-muted text-sm text-foreground/70 leading-relaxed">
                        <div className="font-medium text-foreground mb-2">À quoi servent vos dons ?</div>
                        <ul className="space-y-1 list-disc pl-4">
                            <li>Maintenance et hébergement de la plateforme</li>
                            <li>Modules de formation gratuits (énergie, santé, ONG…)</li>
                            <li>Accompagnement des porteurs de projets africains</li>
                            <li>Développement de nouveaux modules sectoriels</li>
                        </ul>
                    </div>
                </aside>
            </main>
        </div>
    );
}
